import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../../utils/animations';
import debtorApi, { Debtor } from '../../api/debtorApi';
import debtorDocumentApi from '../../api/debtorDocumentApi';

import DocumentValidationHeader from '../../components/debtor/validation/DocumentValidationHeader';
import DocumentValidationStats from '../../components/debtor/validation/DocumentValidationStats';
import DocumentValidationList from '../../components/debtor/validation/DocumentValidationList';
import DocumentChecklist from '../../components/debtor/validation/DocumentChecklist';

interface Document {
  id: number;
  debtor_id: number;
  document_type: string;
}

export interface RequiredDocument {
  type: string;
  label: string;
}

export interface Completeness {
  complete: number;
  total: number;
  percentage: number;
  missing: RequiredDocument[];
}

const requiredDocuments: RequiredDocument[] = [
  { type: 'ktp', label: 'KTP' },
  { type: 'kk', label: 'Kartu Keluarga' },
  { type: 'npwp', label: 'NPWP' },
  { type: 'slip_gaji', label: 'Slip Gaji' },
  { type: 'rekening_koran', label: 'Rekening Koran' },
];

export default function DocumentValidation() {
  const [debtors, setDebtors] = useState<Debtor[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [debtorsData, docsData] = await Promise.all([
        debtorApi.getAll(),
        debtorDocumentApi.getAll(),
      ]);
      setDebtors(debtorsData || []);
      setDocuments(docsData || []);
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  };

  const getDebtorDocuments = (debtorId: number) => {
    return documents.filter(d => d.debtor_id === debtorId);
  };

  const getCompleteness = (debtorId: number): Completeness => {
    const debtor = debtors.find(d => d.id === debtorId);
    if (!debtor) {
      return {
        complete: 0,
        total: requiredDocuments.length,
        percentage: 0,
        missing: requiredDocuments,
      };
    }

    // Gunakan document_status dari debitur
    if (debtor.document_status === 'complete') {
      return {
        complete: requiredDocuments.length,
        total: requiredDocuments.length,
        percentage: 100,
        missing: [],
      };
    }

    // For incomplete, simulate missing documents
    const debtorDocs = getDebtorDocuments(debtorId);
    const docTypes = new Set(debtorDocs.map(d => d.document_type));
    const hasCount =
      debtorDocs.length > 0 ? requiredDocuments.filter(req => docTypes.has(req.type)).length : 0;

    return {
      complete: hasCount,
      total: requiredDocuments.length,
      percentage: Math.round((hasCount / requiredDocuments.length) * 100),
      missing: requiredDocuments.filter(req => !docTypes.has(req.type)),
    };
  };

  const stats = {
    complete: debtors.filter(d => d.document_status === 'complete').length,
    incomplete: debtors.filter(d => d.document_status === 'incomplete').length,
    noDocuments: debtors.filter(d => !d.document_status || d.document_status === 'incomplete')
      .length,
  };

  const incompleteDebtors = debtors.filter(d => d.document_status !== 'complete');

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <DocumentValidationHeader />
      </motion.div>

      <motion.div variants={itemVariants}>
        <DocumentValidationStats stats={stats} />
      </motion.div>

      <motion.div variants={itemVariants}>
        <DocumentValidationList
          debtors={incompleteDebtors}
          loading={loading}
          getCompleteness={getCompleteness}
        />
      </motion.div>

      <motion.div variants={itemVariants}>
        <DocumentChecklist requiredDocuments={requiredDocuments} />
      </motion.div>
    </motion.div>
  );
}
